// AI Logic for Computer opponent
export const AI_DIFFICULTIES = {
  EASY: 'easy',
  MEDIUM: 'medium', 
  HARD: 'hard'
};

// AI card selection logic
export const selectAICard = (aiCards, playerCard, difficulty, gameState = {}) => {
  if (!aiCards || aiCards.length === 0) {
    console.error('AI has no cards to select from');
    return null;
  }

  console.log(`AI selecting card with difficulty: ${difficulty}, cards available: ${aiCards.length}`);

  try {
    switch (difficulty) {
      case AI_DIFFICULTIES.EASY:
        return selectRandomCard(aiCards);
      
      case AI_DIFFICULTIES.MEDIUM:
        return selectMediumCard(aiCards, playerCard, gameState);
      
      case AI_DIFFICULTIES.HARD:
        return selectHardCard(aiCards, playerCard, gameState);
      
      default:
        return selectRandomCard(aiCards);
    }
  } catch (error) {
    console.error('Error in AI card selection:', error);
    return selectRandomCard(aiCards);
  }
};

// Easy AI - Random selection
const selectRandomCard = (cards) => {
  if (!cards || cards.length === 0) return null;
  const randomIndex = Math.floor(Math.random() * cards.length);
  return cards[randomIndex];
};

// Medium AI - Basic strategy
const selectMediumCard = (cards, playerCard, gameState) => {
  if (!cards || cards.length === 0) return null;
  
  if (!playerCard) {
    // AI plays first - play a middle-strength card
    const sortedCards = [...cards].sort((a, b) => a.rank - b.rank);
    const middleIndex = Math.floor(sortedCards.length / 2);
    return sortedCards[middleIndex];
  }

  // Player played first - AI responds
  const winningCards = cards.filter(card => card.rank < playerCard.rank);
  
  if (winningCards.length > 0) {
    // AI can win - play the weakest winning card (highest rank among winners)
    return winningCards.reduce((weakest, card) => 
      card.rank > weakest.rank ? card : weakest
    );
  } else {
    // AI cannot win - play the weakest card overall (highest rank)
    return cards.reduce((weakest, card) => 
      card.rank > weakest.rank ? card : weakest
    );
  }
};

// Hard AI - Advanced strategy
const selectHardCard = (cards, playerCard, gameState) => {
  if (!playerCard) {
    // AI plays first - strategic opening
    const { playerTricks = 0, aiTricks = 0 } = gameState;
    
    if (aiTricks >= 3) {
      // AI is close to winning - play conservatively
      const sortedCards = [...cards].sort((a, b) => b.rank - a.rank);
      return sortedCards[Math.floor(sortedCards.length / 2)];
    } else if (playerTricks >= 3) {
      // Player is close to winning - play aggressively with strongest cards
      return cards.reduce((strongest, card) => 
        card.rank < strongest.rank ? card : strongest
      );
    } else {
      // Mid-game - balanced approach
      const sortedCards = [...cards].sort((a, b) => a.rank - b.rank);
      const targetIndex = Math.floor(sortedCards.length * 0.3); // Play upper-mid strength
      return sortedCards[targetIndex];
    }
  }

  // Player played first - sophisticated response
  const winningCards = cards.filter(card => card.rank < playerCard.rank);
  
  if (winningCards.length > 0) {
    // AI can win the trick
    const { playerTricks = 0, aiTricks = 0 } = gameState;
    
    if (playerTricks >= 3) {
      // Player close to winning - AI must win with strongest possible card
      return winningCards.reduce((strongest, card) => 
        card.rank < strongest.rank ? card : strongest
      );
    } else {
      // Normal play - win with weakest winning card to save stronger cards
      return winningCards.reduce((weakest, card) => 
        card.rank > weakest.rank ? card : weakest
      );
    }
  } else {
    // AI cannot win - play strategically weak card
    const { playerTricks = 0 } = gameState;
    
    if (playerTricks >= 2) {
      // Player has momentum - throw away weakest card
      return cards.reduce((weakest, card) => 
        card.rank > weakest.rank ? card : weakest
      );
    } else {
      // Safe to throw medium-strength card
      const sortedCards = [...cards].sort((a, b) => b.rank - a.rank);
      const safeIndex = Math.floor(sortedCards.length * 0.6);
      return sortedCards[safeIndex];
    }
  }
};

// Get AI difficulty display info
export const getAIDifficultyInfo = (difficulty) => {
  switch (difficulty) {
    case AI_DIFFICULTIES.EASY:
      return { name: 'Easy AI', color: 'text-green-400', icon: '🎲' };
    case AI_DIFFICULTIES.MEDIUM:
      return { name: 'Medium AI', color: 'text-yellow-400', icon: '🎯' };
    case AI_DIFFICULTIES.HARD:
      return { name: 'Hard AI', color: 'text-red-400', icon: '👑' };
    default:
      return { name: 'AI', color: 'text-gray-400', icon: '🤖' };
  }
};